import Papa from "papaparse";

const CSV_FILES = {
  TOOLS: "tools.csv",
  RESOS: "resos.csv",
  IDEAZ: "Ideaz.csv",
  APPS: "apps.csv",
};

let cache = null;

function resetCache() {
  cache = { TOOLS: null, RESOS: null, IDEAZ: null, APPS: null };
}

resetCache();

function publicUrl(path) {
  const base = process.env.PUBLIC_URL || "";
  return `${base}${path}`;
}

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function splitList(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeTag(s) {
  return String(s || "")
    .replace(/\s+/g, "")
    .toLowerCase();
}

/** Same rules as ItemCard.ensureAbsoluteHttpUrl — bare domains become https. */
function normalizeHttpHref(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (/^(mailto:|tel:)/i.test(s)) return s;
  if (/\s/.test(s)) return s;
  if (s.startsWith("/") || s.startsWith(".")) return s;
  if (
    s.includes(".") &&
    /^[\w.-]+(?:\/[\w./?#&=%+~!$'()[\]*,-]*)?$/i.test(s)
  ) {
    return `https://${s}`;
  }
  return s;
}

function appStoreUrls(row) {
  const ios = normalizeHttpHref(
    row.iosLink ||
      row.ios_link ||
      row.appleLink ||
      row.apple_store ||
      ""
  );
  const android = normalizeHttpHref(
    row.androidLink ||
      row.android_link ||
      row.googlePlay ||
      row.google_play ||
      ""
  );
  return { ios, android };
}

function platformsFromRow(row, category) {
  if (category === "apps") {
    const { ios, android } = appStoreUrls(row);
    const pl = [];
    if (ios) pl.push("Apple");
    if (android) pl.push("Android");
    return pl;
  }
  return splitList(row.platform);
}

function rowToItem(row, category, index) {
  const name = (row.name || "").trim();
  const slug = slugify(name) || `row-${index}`;
  const legacyRaw = (row.legacyId || row.legacy_id || row.mongoId || "").trim();
  const { ios, android } = appStoreUrls(row);
  return {
    _id: `csv-${category}-${slug}`,
    _source: "csv",
    category,
    name,
    link: normalizeHttpHref(row.link || ""),
    linkApple: ios,
    linkAndroid: android,
    description: [(row.engDescription || "").trim(), (row.jpDescription || "").trim()],
    tags: splitList(row.tags),
    filterTag: (row.filterTag || "").trim(),
    platform: platformsFromRow(row, category),
    price: (row.price || "").trim(),
    img: (row.img || "").trim(),
    // PocketBase favorites use category "apps"; CSV may still carry a legacy ideaz id.
    category_id:
      category === "apps"
        ? "65a2912599ed3cb8b9886940"
        : (row.category_id || "").trim(),
    ...(legacyRaw ? { legacyId: legacyRaw } : {}),
  };
}

async function loadCategoryItems(categoryUpper) {
  const key = categoryUpper.toUpperCase();
  if (cache[key]) return cache[key];
  const filename = CSV_FILES[key];
  if (!filename) return [];
  const url = publicUrl(`/data_cards/${filename}`);
  const res = await fetch(url);
  if (!res.ok) {
    console.warn("[csvCardCatalog] fetch failed", url, res.status);
    cache[key] = [];
    return cache[key];
  }
  const text = await res.text();
  const parsed = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
  });
  const rows = parsed.data || [];
  const cat = key.toLowerCase();
  cache[key] = rows.map((row, i) => rowToItem(row, cat, i));
  return cache[key];
}

function matchesSearch(search, item) {
  const q = (search || "").trim().toLowerCase();
  if (!q) return true;
  const blob = [
    item.name,
    item.description?.[0],
    item.description?.[1],
    ...(item.tags || []),
    item.filterTag,
    item.linkApple,
    item.linkAndroid,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return blob.includes(q);
}

function matchesTag(tag, item) {
  const t = tag || "";
  if (!t || t === "ALL" || t === "Favs") return true;
  const sel = normalizeTag(t);
  const filterParts = splitList(item.filterTag).map(normalizeTag);
  if (filterParts.some((p) => p && (p === sel || p.includes(sel) || sel.includes(p))))
    return true;
  const itemTags = (item.tags || []).map(normalizeTag);
  if (itemTags.some((p) => p && (p === sel || p.includes(sel) || sel.includes(p))))
    return true;
  return false;
}

function matchesPlatform(filters, item) {
  const f = (filters || []).filter(Boolean);
  if (f.length === 0) return true;
  const platforms = (item.platform || []).map((p) => p.toLowerCase());
  return f.some((pf) => {
    const pfl = (pf || "").toLowerCase();
    return platforms.some((pl) => pl === pfl || pl.includes(pfl) || pfl.includes(pl));
  });
}

function matchesPrice(filters, item) {
  const f = (filters || []).filter(Boolean);
  if (f.length === 0) return true;
  const pr = (item.price || "").toLowerCase();
  return f.some((x) => pr === (x || "").toLowerCase());
}

/**
 * Filter + paginate catalog modes from CSV (see src/data_cards → public/data_cards).
 */
export async function queryCsvItems({
  category,
  tag,
  search,
  platform,
  price,
  page,
  limit,
}) {
  const cat = category.toUpperCase();
  let list = await loadCategoryItems(cat);
  list = list.filter((it) => matchesSearch(search, it));
  list = list.filter((it) => matchesTag(tag, it));
  if (cat === "TOOLS") {
    list = list.filter((it) => matchesPlatform(platform, it));
    list = list.filter((it) => matchesPrice(price, it));
  }
  if (cat === "APPS") {
    list = list.filter((it) => matchesPlatform(platform, it));
  }
  const total = list.length;
  const start = Math.max(0, (page - 1) * limit);
  const items = list.slice(start, start + limit);
  return {
    items,
    totalItems: total,
    totalItemsNumber: total,
    page,
  };
}

export { loadCategoryItems, resetCache };
